import React, { FC, useCallback, useEffect, useState } from 'react';
import { useRedirectLink } from '../../utils';
import { useSDMRouterContext } from '@ldp/sdm-contexts';
import { IabAd } from '@ldp-web/component-library/ui/molecules/iab-ad';
import { IComponentCommonProps } from '@ldp/common-types';
import { paramCase, useTrackMediaAnalyticsAdClick } from '@ldp/media-analytics';
import { IMediaWrapperComponentProps, MediaWrapperComponent } from '../media-wrapper/media-wrapper.component';
import { IMediaIabDisplayProps } from './media-iab-component.interface';
import { IabAdSkeleton } from '@ldp-web/component-library/ui/skeletons/iab-ad-skeleton';
import { WrapperSection } from '@ldp-web/component-library/ui/atoms/section';
import { useViewResolverContext } from '@ldp/view-resolver-lite';

export interface IMediaIabComponentProps extends IMediaIabDisplayProps, IComponentCommonProps {
  componentId: string;
  contentId: string;
}

/** Media Next Gen IAB Ad Component (GAM banner ad replacement) */
export const MediaIabComponentInner: FC<IMediaIabComponentProps> = (props: IMediaIabComponentProps) => {
  const { router } = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const trackAdClick = useTrackMediaAnalyticsAdClick(true);

  const trackFn = useCallback((): void => {
    trackAdClick(props as unknown as Record<string, unknown>, undefined, {
      creative_id: paramCase(callToAction?.text || ''),
    });
  }, []);

  const {
    adSize,
    callToAction,
    desktopImage,
    isSponsored,
    mobileImage,
    sponsoredText,
    tabletImage,
    contentId,
    componentId,
  } = props;

  const inputProps = {
    adSize,
    mobileImage,
    tabletImage,
    desktopImage,
    sponsoredText,
    callToAction,
    contentId,
    isSponsored,
    trackingFn: trackFn,
    redirectAction: redirectToPage,
    componentId,
  };

  return <IabAd {...inputProps} />;
};

const isSSR = typeof document === 'undefined' || typeof window === 'undefined';

const MediaIabComponentInnerWrapped = MediaWrapperComponent(MediaIabComponentInner);

export const MediaIabComponent = (props: JSX.IntrinsicAttributes & object & IMediaWrapperComponentProps) => {
  const [isClient, setIsClient] = useState(false);
  const { fetchOptions } = useViewResolverContext();

  useEffect(() => {
    if (!isSSR) {
      setIsClient(true);
    }
  }, []);

  if (isSSR || !isClient || !fetchOptions) {
    return (
      <WrapperSection>
        <IabAdSkeleton />
      </WrapperSection>
    );
  }

  return <MediaIabComponentInnerWrapped {...props} />;
};